import React, {FC, useState} from 'react'
import {useIntl} from 'react-intl'
import {KTIcon} from '../../../helpers'

interface PurchaseSignalModalProps {
  onClose: () => void
  onConfirm: () => void
  signalTitle: string
  signalPrice?: number
  userWallet?: number
  loading?: boolean
}

const PurchaseSignalModal: FC<PurchaseSignalModalProps> = ({
  onClose,
  onConfirm,
  signalTitle,
  signalPrice = 0,
  userWallet = 0,
  loading = false,
}) => {
  const intl = useIntl()
  const [showConfirm, setShowConfirm] = useState(false)

  const handleConfirmClick = () => {
    if (!showConfirm) {
      setShowConfirm(true)
      return
    }
    onConfirm()
  }

  const insufficientFunds = userWallet < signalPrice
  const localeStr = intl.locale === 'fa' ? 'fa-IR' : 'en-US'

  return (
    <>
      <div className='modal fade show d-block' id='kt_modal_purchase_signal' aria-hidden='true'>
        <div className='modal-dialog modal-dialog-centered mw-650px'>
          <div className='modal-content'>
            <div className='modal-header pb-0 border-0 justify-content-end'>
              <div
                onClick={onClose}
                className='btn btn-sm btn-icon btn-active-color-primary'
                data-bs-dismiss='modal'
                style={{minHeight: '44px', touchAction: 'manipulation'}}
              >
                <KTIcon iconName='cross' className='fs-1' />
              </div>
            </div>

            <div className='modal-body scroll-y mx-5 mx-xl-18 pt-0 pb-15'>
              <div className='text-center mb-13'>
                <KTIcon iconName='information-5' className='fs-2x text-primary mb-5' />
                <h1 className='mb-3'>{intl.formatMessage({id: 'PURCHASE_SIGNAL.TITLE'})}</h1>
                <div className='text-gray-700 fw-bold fs-5 mb-5'>
                  {intl.formatMessage({id: 'PURCHASE_SIGNAL.CONFIRM_QUESTION'})}
                </div>
                <div className='card bg-light-primary mb-5'>
                  <div className='card-body p-4'>
                    <div className='text-gray-900 fw-bold fs-4 mb-2'>{signalTitle}</div>
                    {signalPrice > 0 && (
                      <div className='d-flex justify-content-between align-items-center mt-3'>
                        <span className='text-gray-700 fs-6'>{intl.formatMessage({id: 'PURCHASE_SIGNAL.PRICE_LABEL'})}</span>
                        <span className='text-primary fw-bold fs-5'>
                          {signalPrice.toLocaleString(localeStr)} {intl.formatMessage({id: 'DASHBOARD.CREDIT_UNIT'})}
                        </span>
                      </div>
                    )}
                    <div className='d-flex justify-content-between align-items-center mt-2'>
                      <span className='text-gray-700 fs-6'>{intl.formatMessage({id: 'PURCHASE_SIGNAL.WALLET_LABEL'})}</span>
                      <span className={`fw-bold fs-5 ${insufficientFunds ? 'text-danger' : 'text-success'}`}>
                        {userWallet.toLocaleString(localeStr)} {intl.formatMessage({id: 'DASHBOARD.CREDIT_UNIT'})}
                      </span>
                    </div>
                    {insufficientFunds && (
                      <div className='alert alert-danger mt-3 mb-0'>
                        {intl.formatMessage({id: 'PURCHASE_SIGNAL.INSUFFICIENT_FUNDS'})}
                      </div>
                    )}
                  </div>
                </div>

                {showConfirm && !insufficientFunds && (
                  <div className='alert alert-warning mb-5'>
                    <div className='fw-bold mb-2'>{intl.formatMessage({id: 'PURCHASE_SIGNAL.FINAL_CONFIRM_TITLE'})}</div>
                    <div className='text-gray-700'>
                      {intl.formatMessage({id: 'PURCHASE_SIGNAL.FINAL_CONFIRM_DESC'})}
                    </div>
                  </div>
                )}
              </div>

              <div className='d-flex flex-stack gap-2'>
                <button
                  type='button'
                  onClick={onClose}
                  className='btn btn-light btn-active-light-primary fw-semibold me-2'
                  disabled={loading}
                  style={{minHeight: '44px', touchAction: 'manipulation'}}
                >
                  {intl.formatMessage({id: 'UPGRADE.CANCEL'})}
                </button>
                <button
                  type='button'
                  onClick={handleConfirmClick}
                  className='btn btn-primary fw-semibold'
                  disabled={loading || insufficientFunds}
                  style={{minHeight: '44px', touchAction: 'manipulation'}}
                >
                  {loading ? (
                    <>
                      <span className='spinner-border spinner-border-sm me-2' />
                      {intl.formatMessage({id: 'ADD_CREDIT.SUBMITTING'})}
                    </>
                  ) : showConfirm ? (
                    intl.formatMessage({id: 'PURCHASE_SIGNAL.CONFIRM_BUTTON'})
                  ) : (
                    intl.formatMessage({id: 'PURCHASE_SIGNAL.CONTINUE_BUTTON'})
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className='modal-backdrop fade show' onClick={onClose}></div>
    </>
  )
}

export {PurchaseSignalModal}
